/**
 * API Service - Centralized HTTP client and API communication
 * Handles authentication, error handling, retry logic, and offline detection
 * Provides generic methods for all API interactions
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import { authService } from './authService';
import config from '../config';
import { logger } from '../utils/logger';

// Types
export interface ApiResponse<T = any> {
  status: 'success' | 'error';
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
    has_more_pages: boolean;
  };
}

export interface ApiError {
  message: string;
  status?: number;
  errors?: Record<string, string[]>;
  details?: any;
  isNetworkError?: boolean;
  isOffline?: boolean;
}

type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _retryCount?: number;
  skipRetry?: boolean;
};

// Configuration from centralized config
const API_BASE_URL = config.API.BASE_URL;
const REQUEST_TIMEOUT = config.API.TIMEOUT;
const MAX_RETRY_ATTEMPTS = config.API.MAX_RETRY_ATTEMPTS;
const RETRY_DELAY = config.API.RETRY_DELAY;

class ApiService {
  private client: AxiosInstance;
  private isOnline: boolean = navigator.onLine;
  private retryQueue: Array<() => Promise<any>> = [];
  private inflightSignatures: Map<string, { count: number; startedAt: number }> = new Map();

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
    this.setupNetworkDetection();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config) => {
        this.trackDuplicateRequest(config);
        const token = authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          logger.child('API').warn('No auth token found for request:', config.url);
        }
        return config;
      },
      (error) => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor - Handle responses and errors
    this.client.interceptors.response.use(
      (response) => {
        this.untrackDuplicateRequest(response.config);
        return response;
      },
      async (error) => {
        if (error?.config) {
          this.untrackDuplicateRequest(error.config);
        }
        const originalRequest = error.config as RetryableRequestConfig;

        // Handle 401 Unauthorized - Token expired
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Attempt to refresh using refresh token
            await authService.refreshToken();
            const token = authService.getToken();
            if (token) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed - clear auth state and let app handle redirect
            logger.child('API').error('Token refresh failed, logging out:', refreshError);
            await authService.logout();
            
            // Dispatch custom event for auth failure instead of hard redirect
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
            
            return Promise.reject(this.handleError(refreshError));
          }
        }

        // Handle network errors with retry
        if (this.isNetworkError(error) && !originalRequest.skipRetry && !originalRequest._retryCount) {
          return this.retryRequest(originalRequest, error);
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private makeRequestSignature(requestConfig: AxiosRequestConfig): string {
    const method = (requestConfig.method || 'get').toLowerCase();
    const url = requestConfig.baseURL
      ? `${requestConfig.baseURL}${requestConfig.url || ''}`
      : (requestConfig.url || '');
    const params = requestConfig.params ? JSON.stringify(requestConfig.params) : '';
    return `${method} ${url} ${params}`.trim();
  }

  private trackDuplicateRequest(requestConfig: AxiosRequestConfig): void {
    if (!config.isDevelopment?.()) {
      return;
    }

    const signature = this.makeRequestSignature(requestConfig);
    const existing = this.inflightSignatures.get(signature);
    const startedAt = Date.now();

    if (!existing) {
      this.inflightSignatures.set(signature, { count: 1, startedAt });
      (window as any).__apiInflight = this.inflightSignatures;
      return;
    }

    const nextCount = existing.count + 1;
    this.inflightSignatures.set(signature, { count: nextCount, startedAt: existing.startedAt });
    (window as any).__apiInflight = this.inflightSignatures;

    logger.child('API').warn('Duplicate in-flight request detected', {
      signature,
      count: nextCount,
      ageMs: startedAt - existing.startedAt,
    });
  }

  private untrackDuplicateRequest(requestConfig: AxiosRequestConfig): void {
    if (!config.isDevelopment?.()) {
      return;
    }

    const signature = this.makeRequestSignature(requestConfig);
    const existing = this.inflightSignatures.get(signature);
    if (!existing) {
      return;
    }

    const nextCount = existing.count - 1;
    if (nextCount <= 0) {
      this.inflightSignatures.delete(signature);
    } else {
      this.inflightSignatures.set(signature, { count: nextCount, startedAt: existing.startedAt });
    }
    (window as any).__apiInflight = this.inflightSignatures;
  }

  /**
   * Setup network detection
   */
  private setupNetworkDetection(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processRetryQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Check if error is a network error
   */
  private isNetworkError(error: AxiosError): boolean {
    return !error.response && (
      error.code === 'NETWORK_ERROR' ||
      error.code === 'ECONNABORTED' ||
      error.message.includes('Network Error')
    );
  }

  /**
   * Retry failed requests
   */
  private async retryRequest(originalRequest: RetryableRequestConfig, error: AxiosError | ApiError): Promise<any> {
    if (originalRequest.skipRetry) {
      return Promise.reject(this.handleError(error));
    }

    originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

    if (originalRequest._retryCount > MAX_RETRY_ATTEMPTS) {
      return Promise.reject(this.handleError(error));
    }

    // If offline, queue the request
    if (!this.isOnline) {
      return new Promise((resolve, reject) => {
        this.retryQueue.push(async () => {
          try {
            const response = await this.client(originalRequest);
            resolve(response);
          } catch (retryError) {
            reject(this.handleError(retryError));
          }
        });
      });
    }

    // Wait before retrying
    await this.delay(RETRY_DELAY * originalRequest._retryCount);

    try {
      return await this.client(originalRequest);
    } catch (retryError) {
      return this.retryRequest(originalRequest, retryError as AxiosError);
    }
  }

  /**
   * Process queued requests when back online
   */
  private async processRetryQueue(): Promise<void> {
    const queue = [...this.retryQueue];
    this.retryQueue = [];

    for (const request of queue) {
      try {
        await request();
      } catch (error) {
        logger.child('API').error('Queued request failed:', error);
      }
    }
  }

  /**
   * Handle and normalize errors
   */
  private handleError(error: any): ApiError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiResponse>;

      // Network error
      if (!axiosError.response) {
        return {
          message: 'Network error. Please check your connection.',
          isNetworkError: true,
          isOffline: !this.isOnline,
        };
      }

      // HTTP error with response
      const response = axiosError.response;
      const data = response.data as ApiResponse;

      return {
        message: data?.message || `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
        errors: data?.errors,
        details: (data as any)?.details,
        isNetworkError: false,
        isOffline: false,
      };
    }

    // Generic error
    return {
      message: error.message || 'An unexpected error occurred',
      isNetworkError: false,
      isOffline: false,
    };
  }

  /**
   * Delay utility for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generic GET request
   */
  async get<T = any>(
    url: string,
    config?: RetryableRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, config);
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Generic POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: RetryableRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Generic PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: RetryableRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Generic PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: RetryableRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Generic DELETE request
   */
  async delete<T = any>(
    url: string,
    config?: RetryableRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url, config);
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Upload file with progress tracking
   */
  async uploadFile<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } catch (error) {
      throw error; // Error is already handled by interceptor
    }
  }

  /**
   * Get network status
   */
  getNetworkStatus(): { isOnline: boolean; queueLength: number } {
    return {
      isOnline: this.isOnline,
      queueLength: this.retryQueue.length,
    };
  }

  /**
   * Clear retry queue
   */
  clearRetryQueue(): void {
    this.retryQueue = [];
  }

  /**
   * Update base URL
   */
  updateBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Get raw axios instance for advanced usage
   */
  getClient(): AxiosInstance {
    return this.client;
  }

  /**
   * Get public system settings (versioning, update status)
   */
  async getSystemSettings(): Promise<{
    current_version: string;
    min_version: string;
    update_message: string;
    is_mandatory: boolean;
  }> {
    const response = await this.get<{
      settings: {
        current_version: string;
        min_version: string;
        update_message: string;
        is_mandatory: boolean;
      };
    }>('/system/settings');
    if (!response.data) {
      throw new Error('Invalid system settings payload returned from server');
    }
    return response.data.settings;
  }
}

export interface SystemSettings {
  current_version: string;
  min_version: string;
  update_message: string;
  is_mandatory: boolean;
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;